import { useMutation, useQueryClient } from '@tanstack/react-query';
import useToast from '@sdk/common/toast/useToast';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import { ngettext } from '@sdk/lang/gettext';
import { DeleteAnnouncementResponse } from '@sdk/announcements/types/apiTypes';
import useSlingApi from '@sdk/api/useSlingApi';

import { useAnnouncementsQueryCache } from '@web/announcements/hooks/useAnnouncementsQueryCache';
import { getAnnouncementsQueryKey } from '@web/announcements/utils/getQueryKey';

export const useDeleteMultipleAnnouncements = () => {
  const client = useQueryClient();
  const { setDeletedFlag } = useAnnouncementsQueryCache();
  const { announcements } = useSlingApi();
  const { showErrorToast, showSuccessToast } = useToast();

  const mutation = useMutation({
    mutationFn: async (ids: ID[]) => {
      // eslint-disable-next-line
      // @ts-ignore
      const result = await (window.__Promise as typeof Promise).allSettled(
        ids.map((id) => announcements.delete(id))
      );

      const successIds = (
        result.filter((r) => r.status === 'fulfilled') as PromiseFulfilledResult<{
          response: Response;
          data: DeleteAnnouncementResponse;
        }>[]
      ).map((r) => r.value.data.id);
      const failedIds = ids.filter((id) => !successIds.includes(id));

      return {
        failedIds,
        successIds,
      };
    },

    onMutate: async (ids) => {
      ids.forEach((id) => {
        setDeletedFlag(id);
      });

      const queryKey = getAnnouncementsQueryKey();
      await client.cancelQueries(queryKey, { exact: true });
    },
    onSuccess: ({ failedIds, successIds }, ids) => {
      if (failedIds.length) {
        failedIds.forEach((id) => {
          setDeletedFlag(id, false);
        });
        showErrorToast(getErrorForToast({}));
      }

      if (!successIds.length) {
        return;
      }

      showSuccessToast(
        ngettext(
          '%(counter)s announcement deleted',
          '%(counter)s announcements deleted',
          ids.length,
          {
            counter: successIds.length,
          }
        )
      );

      return { failedIds, successIds };
    },
    onError: (error, ids) => {
      ids.forEach((id) => {
        setDeletedFlag(id, false);
      });
      showErrorToast(getErrorForToast(error));
    },
    onSettled: async () => {
      const queryKey = getAnnouncementsQueryKey();
      await client.invalidateQueries(queryKey, { exact: true });
    },
  });

  return mutation;
};
